import { primitive } from 'styles/constants';

import { AppTakeoverConfig, TakeoverMenuSectionConfig, TakeoverPdpItemConfig } from '../types';

import { HeroBottomGradient } from './HeroBottomGradient';
import { ConfirmHeaderOverlay } from './confirm-header-overlay';
import { HeaderFlair } from './header-flair';

export const BROWN = '#803F20';
const GROGU_GREEN = '#AFCF91';
const GROGU_GREEN_DARK = '#95B475';
const SAND = '#FDD07C';
const SAND_LIGHT = '#FDF5E6';

const ITEM_CONFIG_BASE: TakeoverPdpItemConfig = {
  sectionBackgroundColor: SAND_LIGHT,
};

const MEAL_ITEM_CONFIG: TakeoverPdpItemConfig = {
  ...ITEM_CONFIG_BASE,
  cartItemBackgroundImage: require('./meal-cart-item-background.webp'),
  cartItemImage: require('./meal-cart-item.webp'),
};

const KIDS_ITEM_CONFIG: TakeoverPdpItemConfig = {
  ...ITEM_CONFIG_BASE,
  cartItemBackgroundImage: require('./kids-cart-item-background.webp'),
  cartItemImage: require('./kids-cart-item.webp'),
};

const SECTION_CONFIG_BASE: TakeoverMenuSectionConfig = {
  headerBackgroundColor: SAND,
  itemBackgroundColor: SAND_LIGHT,
  footerImage: require('./section-footer.webp'), // mountain background
  footerImageOffset: 60,
};

const MEALS_SECTION_CONFIG: TakeoverMenuSectionConfig = {
  // desert background image, mando profile pic
  ...SECTION_CONFIG_BASE,
  sectionTileBackgroundImage: require('./meals-section-tile-bg.webp'),
  sectionTileImage: require('./meals-section-tile.webp'),
  titleMaxWidth: 125,
  sectionTileImageStyle: {
    width: 100,
    aspectRatio: 1,
  },
};

const KIDS_SECTION_CONFIG: TakeoverMenuSectionConfig = {
  // green background image, grogu profile pic
  ...SECTION_CONFIG_BASE,
  sectionTileBackgroundImage: require('./kids-menu-section-tile-bg.webp'),
  sectionTileImage: require('./kids-section-tile.webp'),
  titleMaxWidth: 125,
  sectionTileImageStyle: {
    width: 100,
    aspectRatio: 1,
  },
};

export const mandalorian: AppTakeoverConfig = {
  menuIcon: require('./menu-icon.webp'),
  menuIconActive: require('./menu-icon-active.webp'),
  primaryActionButton: {
    backgroundColor: GROGU_GREEN,
    borderColor: GROGU_GREEN_DARK,
    borderWidth: 2,
    _text: {
      color: primitive.$bbqBrown,
    },
    _pressed: {
      backgroundColor: '#90B56C',
    },
  },
  homePage: {
    targetSimpleHomePageIds: [
      // dev lunch simple home page
      '6cc4cd5a-9a89-49ca-a683-b5c741359b81',
      // staging/prod SHP
      '55e96472-e610-4279-a2fa-646253da0982',
      // CA
      '2dff225f-1b19-43f0-8377-10e1af4cc9d7',
    ],
    showReplayButton: true,
    containerBackgroundColor: BROWN,
    HeroBottomGradient,
    videoHasAudio: true,
    deliveryButton: {
      _text: {
        color: primitive.$white,
      },
      borderColor: primitive.$white,
    },
  },
  menu: {
    targetSectionIds: {
      section_5603: KIDS_SECTION_CONFIG, // kids
      '8685f044-584e-42cd-abbf-34b7b6b9a5dc': MEALS_SECTION_CONFIG, // Mandalorian & Grogu Menu
      '5f3e07bb-fe83-47e0-be07-436045d66705': MEALS_SECTION_CONFIG, // CA - Mandalorian Menu
      'b021cd76-f960-4251-9530-b775978cc3fc': MEALS_SECTION_CONFIG, // Mandalorian & Grogu Menu - MW
      '87c69f30-1299-439c-9dfd-5d09c8fdb7d2': KIDS_SECTION_CONFIG, // Kings Jr Kids Meals - Mando
      'e16a5fec-06a5-4346-89f1-0ca2dc07b203': KIDS_SECTION_CONFIG, // CA - King Jr.® Kids Meals - Menu
      'a99d2e09-f674-40b6-b99e-42fa562cf2e1': MEALS_SECTION_CONFIG, // CA - Digital Exclusive Bundles - Mando
    },
  },

  pdp: {
    targetMenuItemIds: {
      // Bounty Bundles
      '896585b9-3cef-4380-bda7-2af9e686d1e1': MEAL_ITEM_CONFIG, // Bounty Bundle (locked)
      '1c9e602d-4c2e-448b-9d92-ab464ec5e350': MEAL_ITEM_CONFIG, // Bounty Bundle (unlocked w cup)
      '12a7c772-c8e0-4888-a862-57f38294b0b6': MEAL_ITEM_CONFIG, // Bounty Bundle (unlocked no cup)
      '05e0f386-b263-42c7-ab8a-53cf5212ba06': MEAL_ITEM_CONFIG, // The Mandalorian & Grogu Cargo Bundle
      'ad538c44-6b03-47ef-9523-b321c850871d': MEAL_ITEM_CONFIG, // CA - BBQ Bounty Bundle (No QC)
      'c1417084-5f7c-48c1-b7cf-dc7e30dec936': MEAL_ITEM_CONFIG, // CA - BBQ Bounty Bundle (QC w/o Cup)
      '39e1b07c-48af-4d40-be24-db7e17d8869c': MEAL_ITEM_CONFIG, // CA - BBQ Bounty Bundle (No SS No QC)

      // Lone Bounty Meal
      '799fab28-92f1-4fb6-ad7e-4f42c827456a': MEAL_ITEM_CONFIG, // The Lone Bounty Meal DT
      '71b10e46-fc54-48cb-9690-4cb8c72a74b7': MEAL_ITEM_CONFIG, // The Lone Bounty Meal DLV

      // Grogu's Galactic Combo
      '13a4e48f-6ce9-40da-94f1-08acb4a042c9': MEAL_ITEM_CONFIG, // Grogu's Galactic Combo DT
      'cba0a5d3-5203-4d4f-b43e-04aee826a9f2': MEAL_ITEM_CONFIG, // Grogu's Galactic Combo DLV

      // Galactic Family Bundle
      '8fab5cde-a779-49e9-94a5-70d587e5fdff': MEAL_ITEM_CONFIG, // Galactic Family Bundle DT
      'a098d4a7-0ca0-4994-957d-3a345dbfa5a5': MEAL_ITEM_CONFIG, // Galactic Family Bundle DLV

      // Mando Mini Meal
      '3ff1b195-a95a-4b5a-a6df-629ab7fb23f5': MEAL_ITEM_CONFIG, // The Mando Mini Meal DT/DLV

      // CA - Digital Exclusive Bundles — Mando (Rest of Canada / Quebec)
      '032e7179-b4a3-4c1e-830d-d7f3bf96bd5e': MEAL_ITEM_CONFIG, // CA - Grogu's Galactic Combo (Rest of CA)
      '922a1f87-9e61-4a0c-8ca4-117639f8a020': MEAL_ITEM_CONFIG, // CA - Grogu's Galactic Combo (QC Only)
      'bb8de58a-1c09-4879-9acb-91a4faafe638': MEAL_ITEM_CONFIG, // CA - The Lone Bounty Meal (Rest of CA)
      'f1e76f2c-ce5d-414c-8e20-3806b38a1b1': MEAL_ITEM_CONFIG, // CA - The Lone Bounty Meal (QC Only)
      'd9514b16-84d4-4e51-a079-4868ec4a8749': MEAL_ITEM_CONFIG, // CA - Galactic Family Bundle (Rest of CA)
      '51b82eee-5006-4304-9c82-9e1ce3fda975': MEAL_ITEM_CONFIG, // CA - Galactic Family Bundle (QC Only)
      '984914d4-ee4b-482f-98f1-17a1e7314073': MEAL_ITEM_CONFIG, // CA - The Mandalorian Mini Meal (Rest of CA)
      '9bb95695-10f8-486d-9ba4-b1d1d59e429e': MEAL_ITEM_CONFIG, // CA - The Mandalorian Mini Meal (QC Only)

      // BBQ Bounty Whopper with Collectible Cup
      'e4bc5527-11e3-4a52-89a0-de31e185a2c1': MEAL_ITEM_CONFIG, // BBQ Bounty Whopper Meal with Collectible Cup (picker)
      '0253a803-386c-411b-9c78-d0e62d70d92f': MEAL_ITEM_CONFIG, // BBQ Bounty Whopper with Collectible Cup Small Combo
      '96309ed6-845d-4d6c-b200-b2cdf5031978': MEAL_ITEM_CONFIG, // BBQ Bounty Whopper with Collectible Cup Medium Combo
      '665f4a52-4e19-42ec-a0f2-cdeeb7cf6f07': MEAL_ITEM_CONFIG, // BBQ Bounty Whopper with Collectible Cup Large Combo

      // BBQ Bounty Whopper
      'c2b9b0b9-f4e5-411e-baee-f2837a77c4d5': MEAL_ITEM_CONFIG, // BBQ Bounty Whopper (picker)
      item_98808: MEAL_ITEM_CONFIG, // BBQ Bounty Whopper (item)
      '9aacaabb-764f-44a0-bae8-76d996ad1f1d': MEAL_ITEM_CONFIG, // BBQ Bounty Whopper Meal Small
      '505f2d7d-4494-4705-9469-fec60a2d6d5a': MEAL_ITEM_CONFIG, // BBQ Bounty Whopper Meal Medium
      '03299d20-e40c-486b-8d0c-60d36c0ed066': MEAL_ITEM_CONFIG, // BBQ Bounty Whopper Meal Large
      '7c120f1a-92c5-46cb-a3cf-15ab4fd66032': MEAL_ITEM_CONFIG, // BBQ Bounty Whopper Meal (picker)
      item_100926: MEAL_ITEM_CONFIG, // CA - BBQ Bounty Whopper® (ALC)
      'a75a4766-1d09-47e2-b39d-1e7e815ac8e6': MEAL_ITEM_CONFIG, // CA - BBQ Bounty Whopper® Medium Cup Combo (No QC)
      '4d75f386-ae1a-409e-bec2-2ba36bc18365': MEAL_ITEM_CONFIG, // CA - BBQ Bounty Whopper® Large Cup Combo (No QC)
      '9607d1f7-1c96-4d28-8562-e118831d0477': MEAL_ITEM_CONFIG, // CA - BBQ Bounty Whopper® Medium Combo (QC w/o Cup)
      'cbcb832a-8a97-4a24-9167-595c70c8bc45': MEAL_ITEM_CONFIG, // CA - BBQ Bounty Whopper® Large Combo (QC w/o Cup)

      // 12 Pc. Grogu's Garlic Chicken Fries with Collectible Cup
      '1a6f9a8c-932a-4fcf-aff3-c98dbc7cb1ef': MEAL_ITEM_CONFIG, // 12 Pc. Grogu's Garlic Chicken Fries Meal with Collectible Cup (picker)
      'd0b59a70-d6df-4a3e-9df6-249f06b8a9c0': MEAL_ITEM_CONFIG, // 12 Pc. Grogu's Garlic Chicken Fries with Collectible Cup Small Combo
      'f6bc61cd-d7f9-4660-9db2-d90c209662d4': MEAL_ITEM_CONFIG, // 12 Pc. Grogu's Garlic Chicken Fries with Collectible Cup Medium Combo
      '14b6a6d4-8682-444c-8246-63fafcb455d3': MEAL_ITEM_CONFIG, // 12 Pc. Grogu's Garlic Chicken Fries with Collectible Cup Large Combo

      // Grogu's Garlic Chicken Fries
      '22f174a3-666e-4e84-8deb-70fc495198cd': MEAL_ITEM_CONFIG, // Grogu's Garlic Chicken Fries (picker)
      item_98820: MEAL_ITEM_CONFIG, // 4 Pc. Grogu's Garlic Chicken Fries
      item_98821: MEAL_ITEM_CONFIG, // 8 Pc. Grogu's Garlic Chicken Fries
      '645c9d94-1f43-4963-8f9d-3f6b1a64e120': MEAL_ITEM_CONFIG, // 8 Pc. Grogu's Garlic Chicken Fries Meal Small
      'f553834b-d976-40e1-aa7d-0fd7b986e23c': MEAL_ITEM_CONFIG, // 8 Pc. Grogu's Garlic Chicken Fries Meal Medium
      '8ec40d59-a025-4215-b599-cb4cd5e18f78': MEAL_ITEM_CONFIG, // 8 Pc. Grogu's Garlic Chicken Fries Meal Large
      item_98822: MEAL_ITEM_CONFIG, // 12 Pc. Grogu's Garlic Chicken Fries
      '3936caa1-9e89-482e-80e2-bc8117ab4688': MEAL_ITEM_CONFIG, // 12 Pc. Grogu's Garlic Chicken Fries Meal Small
      '664826eb-d3da-482f-947f-7a33f2df1306': MEAL_ITEM_CONFIG, // 12 Pc. Grogu's Garlic Chicken Fries Meal Medium
      '9af5c5d1-c7be-429a-8efe-2ba3f09a560f': MEAL_ITEM_CONFIG, // 12 Pc. Grogu's Garlic Chicken Fries Meal Large
      'a1622827-91e8-4ac0-8949-5e0c20263f94': MEAL_ITEM_CONFIG, // Grogu's Garlic Chicken Fries Meal (picker)
      'f572014e-a22c-4fb9-8847-1d586b9caa2d': MEAL_ITEM_CONFIG, // 8 Pc. Grogu's Garlic Chicken Fries Meal (picker)
      'cecbd2dd-d226-4b79-a214-b386c8eeba6f': MEAL_ITEM_CONFIG, // 12 Pc. Grogu's Garlic Chicken Fries Meal (picker)
      item_100928: MEAL_ITEM_CONFIG, // CA - 4 Pc. Grogu's Garlic Chicken Fries (ALC)
      item_100929: MEAL_ITEM_CONFIG, // CA - 8 Pc. Grogu's Garlic Chicken Fries (ALC)
      '9b693346-1128-45a4-bca3-123207e7b605': MEAL_ITEM_CONFIG, // CA - 8 Pc. Grogu's Garlic Chicken Fries Cup Combo (No QC)
      '1de7a8b1-be31-4919-ada6-a33d2c92abba': MEAL_ITEM_CONFIG, // CA - 8 Pc. Grogu's Garlic Chicken Fries Combo (QC w/o Cup)
      item_100931: MEAL_ITEM_CONFIG, // CA - 12 Pc. Grogu's Garlic Chicken Fries (ALC)
      '3629bdc8-3ee6-4dcd-b6f7-1b540a6433a1': MEAL_ITEM_CONFIG, // CA - 12 Pc. Grogu's Garlic Chicken Fries Cup Combo (No QC)
      'ddae925e-607b-4b07-9ff9-291c3588c77e': MEAL_ITEM_CONFIG, // CA - 12 Pc. Grogu's Garlic Chicken Fries Combo (QC w/o Cup)
      // CA - Garlic Chicken Fries (non-Grogu)
      item_101680: MEAL_ITEM_CONFIG, // CA - 8 Pc. Garlic Chicken Fries
      '8a39239e-7887-4005-ab57-1dd6fa3f0560': MEAL_ITEM_CONFIG, // CA - 8 Pc. Garlic Chicken Fries Combo (QC w/o Cup)
      item_101681: MEAL_ITEM_CONFIG, // CA - 12 Pc. Garlic Chicken Fries
      '32fd34d3-bce1-4e51-942c-c1c7e094a1cf': MEAL_ITEM_CONFIG, // CA - 12 Pc. Garlic Chicken Fries Combo (QC w/o Cup)

      // Imperial Cheddar Ranch Tots
      '54b12946-cdc0-457f-b22c-7a56ef468704': MEAL_ITEM_CONFIG, // Imperial Cheddar Ranch Tots (picker)
      'a37e32f5-bf91-4efd-836b-20e20dfc2b70': MEAL_ITEM_CONFIG, // CA - Imperial Ranch Cheesy Tots (picker)
      item_98823: MEAL_ITEM_CONFIG, // 4 Pc. Imperial Cheddar Ranch Tots
      item_98824: MEAL_ITEM_CONFIG, // 8 Pc. Imperial Cheddar Ranch Tots

      // Grogu's Blue Cookie Shake
      item_98825: MEAL_ITEM_CONFIG, // Grogu's Blue Cookie Shake
      item_100934: MEAL_ITEM_CONFIG, // CA - Grogu's Blue Cookie Shake
      item_101679: MEAL_ITEM_CONFIG, // CA - Blue Cookie Shake

      // Mandalorian boxes & cup
      item_100074: MEAL_ITEM_CONFIG, // Black Mandalorian Adult's Box
      item_100073: KIDS_ITEM_CONFIG, // Orange Mandalorian Kid's Box
      item_100075: MEAL_ITEM_CONFIG, // SLO Mandalorian Cup
      item_100937: MEAL_ITEM_CONFIG, // CA - Mandalorian Collector's Cup

      // Burger King Jr Meals (Kids)
      'c06c5fc0-eb06-4bb4-8971-c81ca5793ecb': KIDS_ITEM_CONFIG, // Burger King Jr Meals (picker)
      '71886320-6d80-410f-8743-ec5f548d9ea4': KIDS_ITEM_CONFIG, // Construct Change - Cheeseburger King Jr. Kids Meal(s)
      'c809e520-5e7a-4933-9fb0-b4044d5c559a': KIDS_ITEM_CONFIG, // Construct Change - Hamburger King Jr. Kids Meal(s)
      '1c18a56b-5b34-4100-8b76-85dc9c45e03c': KIDS_ITEM_CONFIG, // Construct Change NCR - Cheeseburger King Jr. Kids Meal(s)
      'a6a71a64-2025-47ca-9e6f-150f6a7662f5': KIDS_ITEM_CONFIG, // Construct Change - NCR Hamburger King Jr. Kids Meal(s)
      'a608a7fd-0e37-4dce-a86b-cc56c89d93d8': KIDS_ITEM_CONFIG, // Construct Change PAR - Hamburger King Jr. Kids Meal(s)
      '8ca03cd7-e3cf-4e19-8521-7ed0c71b32ef': KIDS_ITEM_CONFIG, // Construct Change - PAR Cheeseburger King Jr. Kids Meal(s)
      item_103813: KIDS_ITEM_CONFIG, // Hamburger King Jr. (item)
      item_103814: KIDS_ITEM_CONFIG, // Cheeseburger King Jr. (item)
      '55856076-94b7-4dfb-b00b-f83b535ea9e7': KIDS_ITEM_CONFIG, // Construct Change Carrols - Cheeseburger King Jr. Kids Meal(s)
      '2120e6c1-d3ee-4373-aff3-08162d355ecf': KIDS_ITEM_CONFIG, // Construct Change Carrols - Hamburger King Jr. Kids Meal(s)

      // Nugget King Jr Meals (Kids)
      '25d3b74c-5a96-4511-ab41-882ee81a89ba': KIDS_ITEM_CONFIG, // Nugget King Jr Meals (picker)
      '49f6f383-9689-47a6-a4b1-2768c6772b7d': KIDS_ITEM_CONFIG, // Construct Change - 4 Pc. Nuggets King Jr. Kids Meal(s)
      'd5e41231-090c-4653-8115-2703173138f4': KIDS_ITEM_CONFIG, // Construct Change - 6 Pc. Nuggets King Jr. Kids Meal(s)
      '8f03d618-07f8-4b56-8089-f4f1085b6326': KIDS_ITEM_CONFIG, // Construct Change - NCR 4 Pc. Nuggets King Jr. Kids Meal(s)
      'd7f764ee-9c35-4579-9208-92583acbdcc2': KIDS_ITEM_CONFIG, // Construct Change - NCR 6 Pc. Nuggets King Jr. Kids Meal(s)
      'f54785b1-f361-4297-b7aa-534d7cfa368d': KIDS_ITEM_CONFIG, // Construct Change Carrols - 4 Pc. Nuggets King Jr. Kids Meal(s)
      '6b5598bb-ac72-4c7e-81dd-1dc59bd8eacc': KIDS_ITEM_CONFIG, // Construct Change Carrols - 6 Pc. Nuggets King Jr. Kids Meal(s)
    },
    headerFontColor: primitive.$white,
    headerBackgroundColor: '#53230B',
    backgroundColor: '#FFF8DC',
    overviewTopImage: require('./pdp-overview-top.webp'),
    overviewBottomImage: require('./pdp-overview-bottom.webp'),
    overviewBottomImageOffset: 100,
    summaryPageBackgroundImage: require('./pdp-summary-page-bg.webp'),
    summaryPageTextColor: primitive.$white,
    priceAndCaloriesFontColor: primitive.$bbqBrown,
    HeaderFlair,
  },
  cart: {
    paymentProcessingVideo: require('./checkout.mp4'),
    paymentProcessingBackgroundColor: '#D97C31',
  },
  orderConfirmDetails: {
    headerImage: require('./order-confirm-details-header.webp'),
    ConfirmHeaderOverlay,
    headerFontColor: primitive.$white,
  },
};
